import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { api } from '../lib/api.js';
import { Badge } from '@/components/ui/badge';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ErrorBanner } from '@/components/feedback';

interface Session {
  id: string;
  agentId: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  error: string | null;
  tokenUsage: {
    input_tokens: number;
    output_tokens: number;
  };
}

interface Agent {
  id: string;
  name: string;
}

export function SessionsView({ onOpen }: { onOpen: (id: string) => void }) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [agents, setAgents] = useState<Record<string, Agent>>({});
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get<Session[]>('/api/sessions?limit=50'),
        api.get<Agent[]>('/api/agents'),
      ]);
      setSessions(s);
      const map: Record<string, Agent> = {};
      for (const x of a) map[x.id] = x;
      setAgents(map);
    } catch (e) {
      setError((e as { message: string }).message);
    }
  };

  useEffect(() => {
    void reload();
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader title="Sessions" description="Recent agent runs. Tap to open the chat." />

      <ItemGroupContent>
        {sessions.length === 0 && <ItemGroupEmpty>no sessions yet</ItemGroupEmpty>}

        {sessions.map((s) => (
          <Item key={s.id} interactive onClick={() => onOpen(s.id)}>
            <ItemMedia>
              <History />
            </ItemMedia>
            <ItemContent>
              <ItemHeader>
                <ItemTitle>{agents[s.agentId]?.name ?? s.agentId.slice(0, 8)}</ItemTitle>
                <Badge variant={statusVariant(s.status)}>{s.status}</Badge>
              </ItemHeader>
              <ItemDescription className="font-mono">{s.id}</ItemDescription>
              <ItemFooter>
                <span>in {s.tokenUsage.input_tokens}</span>
                <span>·</span>
                <span>out {s.tokenUsage.output_tokens}</span>
                {s.error && (
                  <>
                    <span>·</span>
                    <span className="text-destructive">{s.error}</span>
                  </>
                )}
              </ItemFooter>
            </ItemContent>
            <ItemActions>
              <span className="font-mono text-xs text-muted-foreground">{s.startedAt.slice(11, 19)}</span>
            </ItemActions>
          </Item>
        ))}
      </ItemGroupContent>
    </ItemGroup>
  );
}

function statusVariant(
  status: string,
): 'default' | 'destructive' | 'secondary' | 'outline' | 'success' | 'warning' {
  if (status === 'running') return 'success';
  if (status === 'waiting_user') return 'default';
  if (status === 'failed' || status === 'aborted') return 'destructive';
  return 'secondary';
}
